import { useEffect, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { Users, Phone, MapPin, ShoppingBag, UserPlus, Search } from "lucide-react";
import { http, formatPrice } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import AdminTopBar from "@/components/AdminTopBar";

const AdminCustomers = () => {
  const { token, isAdmin } = useAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");

  useEffect(() => {
    if (!isAdmin) return;
    http
      .get("/admin/customers", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [isAdmin]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.customers;
    if (tab === "buyers") rows = rows.filter((c) => c.order_count > 0);
    if (tab === "leads") rows = rows.filter((c) => c.order_count === 0);
    const term = q.trim().toLowerCase();
    if (term)
      rows = rows.filter(
        (c) =>
          c.phone.includes(term) ||
          (c.name || "").toLowerCase().includes(term) ||
          (c.address || "").toLowerCase().includes(term)
      );
    return rows;
  }, [data, q, tab]);

  if (!isAdmin) return <Navigate to="/admin/login" replace />;

  const cards = data
    ? [
        { label: "Total Contacts", value: data.total, icon: Users, testid: "stat-total-contacts" },
        { label: "Buyers", value: data.buyers, icon: ShoppingBag, testid: "stat-buyers" },
        { label: "Leads (no order yet)", value: data.leads, icon: UserPlus, testid: "stat-leads" },
      ]
    : [];

  const tabBtn = (key, label) => (
    <button
      onClick={() => setTab(key)}
      className={`text-sm font-medium px-3 h-9 rounded-sm transition-colors ${
        tab === key ? "bg-[#1e293b] text-white" : "text-[#475569] hover:bg-slate-100"
      }`}
      data-testid={`customers-tab-${key}`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#f1f5f9]" data-testid="admin-customers-page">
      <AdminTopBar />
      <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-4">
        <h1 className="font-head text-xl font-bold text-[#0f172a]">Customer Contact Book</h1>

        {loading ? (
          <div className="py-20 text-center text-[#64748b]">Loading contacts…</div>
        ) : !data ? (
          <div className="py-20 text-center text-[#64748b]" data-testid="customers-error">Could not load contacts.</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              {cards.map((c) => (
                <div key={c.testid} className="bg-white rounded-sm fk-shadow px-4 py-4 flex items-center gap-3" data-testid={c.testid}>
                  <div className="w-10 h-10 rounded-sm bg-[#FCEBEA] flex items-center justify-center shrink-0">
                    <c.icon className="w-5 h-5 text-[#C62828]" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-[#0f172a] leading-none">{c.value}</div>
                    <div className="text-xs text-[#64748b] mt-1">{c.label}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div className="flex items-center gap-1">
                {tabBtn("all", "All")}
                {tabBtn("buyers", "Buyers")}
                {tabBtn("leads", "Leads")}
              </div>
              <div className="relative max-w-xs w-full">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, phone, address"
                  className="w-full h-9 border border-[#e2e8f0] rounded-sm pl-9 pr-3 text-sm outline-none focus:border-[#C62828] bg-white"
                  data-testid="customers-search"
                />
              </div>
            </div>

            <div className="bg-white rounded-sm fk-shadow overflow-hidden" data-testid="customers-table">
              {filtered.length === 0 ? (
                <div className="px-4 py-12 text-center text-sm text-[#64748b]" data-testid="customers-empty">
                  No contacts found.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-[#64748b] border-b border-[#f0f0f0] bg-[#f8fafc]">
                        <th className="px-4 py-2.5 font-medium">Name</th>
                        <th className="px-4 py-2.5 font-medium">Phone</th>
                        <th className="px-4 py-2.5 font-medium">Address</th>
                        <th className="px-4 py-2.5 font-medium">Orders</th>
                        <th className="px-4 py-2.5 font-medium">Spent</th>
                        <th className="px-4 py-2.5 font-medium">Last Seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((c, i) => (
                        <tr key={c.phone} className="border-b border-[#f8fafc] last:border-0 hover:bg-[#f8fafc]" data-testid={`customer-row-${i}`}>
                          <td className="px-4 py-3 font-medium text-[#0f172a]">{c.name || "—"}</td>
                          <td className="px-4 py-3">
                            <a href={`tel:+91${c.phone}`} className="inline-flex items-center gap-1.5 text-[#C62828]" data-testid={`customer-phone-${i}`}>
                              <Phone className="w-3.5 h-3.5" /> {c.phone}
                            </a>
                          </td>
                          <td className="px-4 py-3 text-[#475569] max-w-[320px]">
                            {c.address ? (
                              <span className="flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#94a3b8]" /> {c.address}</span>
                            ) : (
                              <span className="text-[#cbd5e1]">No address</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {c.order_count > 0 ? (
                              <span className="text-xs font-semibold px-2 py-1 rounded-sm bg-[#dcfce7] text-[#15803d]">{c.order_count}</span>
                            ) : (
                              <span className="text-xs font-semibold px-2 py-1 rounded-sm bg-slate-100 text-[#64748b]">Lead</span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-medium text-[#0f172a]">{c.total_spent ? formatPrice(c.total_spent) : "—"}</td>
                          <td className="px-4 py-3 text-xs text-[#64748b] whitespace-nowrap">
                            {c.last_seen ? new Date(c.last_seen).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminCustomers;
